import { useState, useCallback } from 'react';
import type { DecisionAxisData, OptionDetail, InterventionItem, RiskItem, ResolvedItem } from './buildPortalViewModel';

interface DecisionAxisProps {
  data: DecisionAxisData;
  onSwotHighlight?: (nodeId: string) => void;
}

export function DecisionAxis({ data, onSwotHighlight }: DecisionAxisProps) {
  return (
    <section className="decision-axis" aria-label="Decision options">
      <h2 className="decision-axis__heading">Your options</h2>
      <div className="decision-axis__columns">
        <OptionColumn
          option={data.optionA}
          variant="a"
          label="Option A (preferred)"
          onSwotHighlight={onSwotHighlight}
        />
        <OptionColumn
          option={data.optionAPlus}
          variant="a-plus"
          label="Option A+ (with additions)"
          onSwotHighlight={onSwotHighlight}
        />
      </div>

      {/* Option B: Alternative path */}
      {data.optionB ? (
        <div className="decision-axis__alternative">
          <h3 className="decision-axis__alt-heading">Option B (Alternative)</h3>
          <OptionColumn
            option={data.optionB}
            variant="b"
            label="Option B"
            onSwotHighlight={onSwotHighlight}
          />
        </div>
      ) : (
        <div className="decision-axis__alternative decision-axis__alternative--empty">
          <p className="decision-axis__no-alt">No alternative option defined.</p>
        </div>
      )}
    </section>
  );
}

// ─── OptionColumn ────────────────────────────────────────────────

interface OptionColumnProps {
  option: OptionDetail;
  variant: 'a' | 'a-plus' | 'b';
  label: string;
  onSwotHighlight?: (nodeId: string) => void;
}

function OptionColumn({ option, variant, label, onSwotHighlight }: OptionColumnProps) {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleItem = useCallback((id: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  return (
    <div className={`option-column option-column--${variant}`}>
      <div className="option-column__header">
        <span className="option-column__label">{label}</span>
        <h3 className="option-column__title">{option.title}</h3>
        <p className="option-column__subtitle">{option.subtitle}</p>
      </div>

      {/* Problems solved — the goal comes first */}
      {option.problemsSolved.length > 0 && (
        <div className="option-column__section">
          <h4 className="option-column__section-title">What this solves</h4>
          <ul className="option-column__list option-column__list--solved" role="list">
            {option.problemsSolved.map(item => (
              <li key={item.id} className="option-column__solved-item">
                <span className={`option-column__resolution-icon option-column__resolution-icon--${item.resolution}`} aria-hidden="true">
                  {item.resolution === 'resolved' ? '\u2713' : '\u2014'}
                </span>
                {item.label}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* What's included */}
      <div className="option-column__section">
        <h4 className="option-column__section-title">What's included</h4>
        <ul className="option-column__list" role="list">
          {option.included.map(item => (
            <IncludedItem
              key={item.id}
              item={item}
              expanded={expandedItems.has(item.id)}
              onToggle={() => toggleItem(item.id)}
            />
          ))}
        </ul>
      </div>

      {/* Risks remaining */}
      {option.risksRemaining.length > 0 && (
        <div className="option-column__section">
          <h4 className="option-column__section-title">Risks that remain</h4>
          <ul className="option-column__list option-column__list--risks" role="list">
            {option.risksRemaining.map(risk => (
              <RiskItemRow
                key={risk.id}
                risk={risk}
                expanded={expandedItems.has(risk.id)}
                onToggle={() => toggleItem(risk.id)}
                onSwotHighlight={onSwotHighlight}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────

function IncludedItem({ item, expanded, onToggle }: {
  item: InterventionItem;
  expanded: boolean;
  onToggle: () => void;
}) {
  const levelBadge = item.optionLevel === 'a+' ? 'A+ only' :
                     item.optionLevel === 'a' ? 'Essential' : '';
  return (
    <li className="option-column__included-item">
      <button
        className="option-column__item-toggle"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <span className="option-column__item-label">{item.label}</span>
        {levelBadge && (
          <span className={`option-column__level-badge option-column__level-badge--${item.optionLevel}`}>
            {levelBadge}
          </span>
        )}
        <span className="option-column__chevron" aria-hidden="true">
          {expanded ? '\u25B4' : '\u25BE'}
        </span>
      </button>
      {expanded && (
        <p className="option-column__item-detail">{item.explanation}</p>
      )}
    </li>
  );
}

function RiskItemRow({ risk, expanded, onToggle, onSwotHighlight }: {
  risk: RiskItem;
  expanded: boolean;
  onToggle: () => void;
  onSwotHighlight?: (nodeId: string) => void;
}) {
  return (
    <li className="option-column__risk-item">
      <button
        className="option-column__item-toggle option-column__item-toggle--risk"
        onClick={() => {
          onToggle();
          if (risk.swotNodeId && onSwotHighlight) onSwotHighlight(risk.swotNodeId);
        }}
        aria-expanded={expanded}
      >
        <span className="option-column__item-label">{risk.label}</span>
        <span className="option-column__chevron" aria-hidden="true">
          {expanded ? '\u25B4' : '\u25BE'}
        </span>
      </button>
      {expanded && (
        <p className="option-column__item-detail">{risk.detail}</p>
      )}
    </li>
  );
}
